import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import get from 'lodash.get';
import { connect } from 'react-redux';

import {
  CustomPrintConfigurationPhotos,
  PurchasedCustomPrintForConfig,
} from '../../../graphql/queries/custom-prints';
import { purchasedCustomPrintUpdate } from '../../../graphql/mutations/custom-prints';
import { MeWithPurchasedAlbumPrintsByAlbum } from '../../../graphql/queries/account';
import { ORDER_STATUS } from '../../../constants/pro-custom-prints';
import CustomPrintConfiguratorManager from '../../../apps/consumer/components/custom-prints/CustomPrintConfiguratorManager';
import { openModal } from '../../../actions/modal-actions';
import { createCustomPrintCandidatePhotoGroups } from '../../../components/global/CustomPrintConfigurator/helpers';

const propTypes = {
  loading: PropTypes.bool,
  albumCustomPrint: PropTypes.object,
  candidatePhotoGroups: PropTypes.array,
  pendingPurchasedCustomPrints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  customPrintUpdate: PropTypes.func,
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumId: PropTypes.string.isRequired,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};

const CustomPrintConfiguratorContainer = ({ match, ...props }) => {
  const { albumId } = match.params;
  return (
    <CustomPrintConfiguratorManager
      {...props}
      path={`/purchase/album/${albumId}/custom-prints`}
    />
  );
};

CustomPrintConfiguratorContainer.propTypes = propTypes;

const withPurchasedCustomPrint = graphql(PurchasedCustomPrintForConfig, {
  options: ({ match }) => ({
    variables: {
      id: match.params.purchasedCustomPrintId,
    },
  }),
  props: ({ data: { purchasedCustomPrint, loading } }) => ({
    loading,
    albumCustomPrint: purchasedCustomPrint,
  }),
});

const withPurchasedCustomPrintCandidatePhotos = graphql(
  CustomPrintConfigurationPhotos,
  {
    options: ({ match }) => ({
      variables: {
        purchasedCustomPrintId: match.params.purchasedCustomPrintId,
      },
    }),
    props: ({ data: { customPrintConfigurationPhotos, fetchMore } }) => {
      return {
        candidatePhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: customPrintConfigurationPhotos,
          mappings: [
            {
              id: 'matches',
              label: 'Matches',
              path: 'matched_photos_extended',
              mapper: record => record.photo,
            },
            {
              id: 'favorites',
              label: 'Favorites',
              path: 'favorited_photos',
            },
            {
              id: 'allPhotos',
              label: 'All Photos',
              path: 'all_photos',
            },
          ],
          fetchMore,
        }),
      };
    },
  },
);

const withCustomerInputPurchasedCustomPrints = graphql(
  MeWithPurchasedAlbumPrintsByAlbum,
  {
    options: ({ match }) => ({
      variables: {
        albumId: match.params.albumId,
        orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { me } }) => ({
      pendingPurchasedCustomPrints: get(
        me,
        'purchased_custom_prints.records',
        [],
      ),
    }),
    skip: ({ match }) => !match.params.albumId,
  },
);

const withPurchasedCustomPrintUpdateMutation = graphql(
  purchasedCustomPrintUpdate,
  {
    props: ({ mutate, ownProps }) => ({
      customPrintUpdate: ({
        id,
        caption,
        photos,
        placeOrder = true,
        address,
      }) => {
        const addressId = get(address, 'id');

        return mutate({
          variables: {
            input: {
              purchased_custom_print_id: id,
              custom_print_caption: caption,
              photos,
              place_order: placeOrder,
              account_address_id: addressId,
              account_address: addressId ? undefined : address,
            },
          },
          update: store => {
            const { albumId } = ownProps.match.params;

            if (!albumId) {
              return;
            }

            const data = store.readQuery({
              query: MeWithPurchasedAlbumPrintsByAlbum,
              variables: {
                albumId,
                orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
              },
            });

            const purchasedCustomPrints = get(
              data,
              'me.purchased_custom_prints.records',
              [],
            );

            store.writeQuery({
              query: MeWithPurchasedAlbumPrintsByAlbum,
              variables: {
                albumId,
                orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
              },
              data: {
                ...data,
                me: {
                  ...data.me,
                  purchased_custom_prints: {
                    ...data.me.purchased_custom_prints,
                    total: data.me.purchased_custom_prints.total - 1,
                    records: purchasedCustomPrints.filter(x => x.id !== id),
                  },
                },
              },
            });
          },
        });
      },
    }),
  },
);

const mapDispatchToProps = {
  openModal,
};

export default compose(
  withRouter,
  withPurchasedCustomPrint,
  withPurchasedCustomPrintCandidatePhotos,
  withPurchasedCustomPrintUpdateMutation,
  withCustomerInputPurchasedCustomPrints,
  connect(null, mapDispatchToProps),
)(CustomPrintConfiguratorContainer);
